import { Head, Link } from "@inertiajs/react"
import { ArrowLeft, Heart } from "lucide-react"
import { useTranslation } from "react-i18next"

import { KudoCard } from "@/components/crew/kudo-card"
import { EmptyState } from "@/components/empty-state"
import AppLayout from "@/layouts/app-layout"
import { creatorProfilePath } from "@/routes"
import type { RecentKudo } from "@/types/crew"

/**
 * OKS-758 — Creator kudos surface (`/profile/kudos`).
 *
 * The destination for a `creator.kudo.received` notification, and the
 * answer to the ticket: the brand could always read what it wrote
 * (`Brand/Crew/Show`'s RECENT KUDOS strip), but its recipient had no
 * surface rendering the message at all. The notification row is the teaser
 * (200-char preview, then `line-clamp-1 text-xs`); this is where the
 * ≤280-char note is actually readable — `text-sm` on default foreground,
 * verbatim, no clamp. That contrast IS the point of the ticket.
 *
 * Rows reuse `<KudoCard>` UNCHANGED — its own docstring designates it for
 * "any future kudo surface". Do not fork it, do not wrap it in a clamp.
 *
 * # Brand isolation
 *
 * `kudos` are scoped server-side to `Current.membership` AND the current
 * brand AND `kind = recognition` — no client-side query, no filter state,
 * no id on the wire, nothing to widen. The FE never re-sorts and never
 * re-slices: the list is BE-ordered (newest-first) and BE-capped.
 * Switching brand triggers a full Inertia visit (`CrewSwitcher`) which
 * re-renders this page for the new brand.
 *
 * Props ship with the render (not deferred): one indexed query on
 * `[crew_membership_id, created_at]`, so there is no loading state and no
 * error branch — a query failure is a genuine 500.
 */
interface CreatorKudosProps {
  kudos: RecentKudo[]
}

export default function CreatorKudos({ kudos }: CreatorKudosProps) {
  const { t } = useTranslation()

  return (
    <AppLayout>
      <Head title={t("creator_kudos.page_title")} />

      <main
        data-testid="kudos-page"
        className="mx-auto flex w-full max-w-2xl flex-col gap-6 px-4 py-6"
      >
        <header className="flex flex-col gap-3">
          <Link
            href={creatorProfilePath()}
            className="text-muted-foreground hover:text-foreground inline-flex w-fit items-center gap-1.5 text-sm"
            data-testid="kudos-back"
          >
            <ArrowLeft aria-hidden="true" className="size-4" />
            {t("creator_kudos.back")}
          </Link>
          <h1 className="text-2xl font-bold">{t("creator_kudos.title")}</h1>
        </header>

        {kudos.length === 0 ? (
          <EmptyState
            icon={Heart}
            /* The page owns the <h1> above, so the empty state's title must
               step down to keep the single-h1 contract (UX Spec §8). The
               primitive defaults to "h1". */
            titleAs="h2"
            title={t("creator_kudos.empty.title")}
            description={t("creator_kudos.empty.subtitle")}
            role="status"
            data-testid="kudos-empty-state"
          />
        ) : (
          <ul data-testid="kudos-list" className="flex flex-col gap-3">
            {kudos.map((kudo) => (
              <li key={kudo.id}>
                <KudoCard kudo={kudo} />
              </li>
            ))}
          </ul>
        )}
      </main>
    </AppLayout>
  )
}
